import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { getCashFlowData, getExpenseBreakdown, getRevenueTrend } from '../../../services/dashboardService';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { formatCurrency } from '../../../utils/currency';


const ChartSection = () => {
  const { data: currentCompany } = useCurrentCompany();
  const [selectedChart, setSelectedChart] = useState('cashflow');
  const [dateRange, setDateRange] = useState('6months');
  const [cashFlowData, setCashFlowData] = useState([]);
  const [expenseBreakdownData, setExpenseBreakdownData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentCompany) {
      loadChartData();
    }
  }, [currentCompany]);

  const formatAmountWithCurrency = (amount) => {
    if (!currentCompany?.currency) {
      return formatCurrency(amount);
    }
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currentCompany.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (error) {
      console.warn('Invalid currency code:', currentCompany.currency, 'falling back to formatCurrency');
      return formatCurrency(amount);
    }
  };

  const loadChartData = async () => {
    setLoading(true);
    try {
      const [cashFlow, expenseBreakdown, revenue] = await Promise.all([
        getCashFlowData(currentCompany?.id),
        getExpenseBreakdown(currentCompany?.id),
        getRevenueTrend(currentCompany?.id)
      ]);
      setCashFlowData(cashFlow);
      setExpenseBreakdownData(expenseBreakdown);
      setRevenueData(revenue);
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Custom tooltip formatter for currency values
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatAmountWithCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatAmountWithCurrency(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const chartTypes = [
    { id: 'cashflow', label: 'Cash Flow', icon: 'TrendingUp' },
    { id: 'expenses', label: 'Expense Breakdown', icon: 'PieChart' },
    { id: 'revenue', label: 'Revenue Trend', icon: 'BarChart3' }
  ];

  const dateRanges = [
    { id: '3months', label: '3 Months' },
    { id: '6months', label: '6 Months' },
    { id: '1year', label: '1 Year' },
    { id: 'custom', label: 'Custom' }
  ];

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
          Financial Analytics
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
            {chartTypes?.map((chart) => (
              <button
                key={chart?.id}
                onClick={() => setSelectedChart(chart?.id)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md font-body text-sm font-medium
                  transition-smooth hover-lift active-press whitespace-nowrap flex-shrink-0
                  ${selectedChart === chart?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                  }
                `}
              >
                <Icon name={chart?.icon} size={16} />
                <span>{chart?.label}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
            {dateRanges?.map((range) => (
              <button
                key={range?.id}
                onClick={() => setDateRange(range?.id)}
                className={`
                  px-3 py-2 rounded-md font-body text-sm font-medium transition-smooth
                  hover-lift active-press whitespace-nowrap flex-shrink-0
                  ${dateRange === range?.id
                    ? 'bg-secondary text-secondary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                  }
                `}
              >
                {range?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full h-64 md:h-80 lg:h-96" aria-label={`${chartTypes?.find(c => c?.id === selectedChart)?.label} Chart`}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              <p className="mt-4 text-muted-foreground">Loading chart data...</p>
            </div>
          </div>
        ) : (
          <>
            {selectedChart === 'cashflow' && (
              <ResponsiveContainer width="100%" height="100%">
                {cashFlowData?.length > 0 ? (
                  <LineChart data={cashFlowData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="var(--color-muted-foreground)"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="income" 
                      stroke="var(--color-success)" 
                      strokeWidth={2}
                      name="Income"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="expenses" 
                      stroke="var(--color-error)" 
                      strokeWidth={2}
                      name="Expenses"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="var(--color-primary)" 
                      strokeWidth={2}
                      name="Profit"
                    />
                  </LineChart>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">No cash flow data available</p>
                  </div>
                )}
              </ResponsiveContainer>
            )}

            {selectedChart === 'expenses' && (
              <ResponsiveContainer width="100%" height="100%">
                {expenseBreakdownData?.length > 0 ? (
                  <PieChart>
                    <Pie
                      data={expenseBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {expenseBreakdownData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                    <Legend />
                  </PieChart>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">No expense data available</p>
                  </div>
                )}
              </ResponsiveContainer>
            )}

            {selectedChart === 'revenue' && (
              <ResponsiveContainer width="100%" height="100%">
                {revenueData?.length > 0 ? (
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="var(--color-muted-foreground)"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      dataKey="revenue" 
                      fill="var(--color-primary)" 
                      name="Revenue"
                    />
                  </BarChart>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">No revenue data available</p>
                  </div>
                )}
              </ResponsiveContainer>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChartSection;